import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTournamentFinancials, fetchTournamentPotDistribution } from './adminApiService';

const AnalyticsTab = ({
  analyticsTimeRange,
  setAnalyticsTimeRange,
  selectedTournamentForAnalytics,
  setSelectedTournamentForAnalytics,
  analyticsView,
  setAnalyticsView,
  tournaments,
  analyticsData,
  analyticsLoading,
  analyticsError
}) => {
  // Fetch tournament pot distribution data when a specific tournament is selected
  const { data: potDistributionData, isLoading: potDistributionLoading, error: potDistributionError } = useQuery({
    queryKey: ['tournament-pot-distribution', selectedTournamentForAnalytics],
    queryFn: () => fetchTournamentPotDistribution(selectedTournamentForAnalytics),
    enabled: selectedTournamentForAnalytics !== 'all' && selectedTournamentForAnalytics !== '',
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Show tournament selection message when no specific tournament is selected
  if (selectedTournamentForAnalytics === 'all' || !selectedTournamentForAnalytics) {
    return (
      <div className="analytics-section">
        <div className="analytics-header">
          <div className="analytics-controls">
            <div className="analytics-filters">
              <select
                value={selectedTournamentForAnalytics}
                onChange={(e) => setSelectedTournamentForAnalytics(e.target.value)}
                className="admin-form-select"
              >
                <option value="all">Select a Tournament for Financial Analysis</option>
                {tournaments?.map(tournament => (
                  <option key={tournament.id} value={tournament.id}>
                    {tournament.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="analytics-placeholder">
          <div className="placeholder-content">
            <h3>Select a Tournament</h3>
            <p>Choose a tournament from the dropdown above to view its pot distribution data.</p>
            <div className="placeholder-icon">📊</div>
          </div>
        </div>
      </div>
    );
  }

  // Display tournament pot distribution data
  return (
    <div className="analytics-section">
      <div className="analytics-header">
        <div className="analytics-controls">
          <div className="analytics-filters">
            <select
              value={selectedTournamentForAnalytics}
              onChange={(e) => setSelectedTournamentForAnalytics(e.target.value)}
              className="admin-form-select"
            >
              <option value="all">Select a Tournament for Financial Analysis</option>
              {tournaments?.map(tournament => (
                <option key={tournament.id} value={tournament.id}>
                  {tournament.name}
                </option>
              ))}
            </select>
          </div>

          <div className="analytics-views">
            <button
              className={`admin-btn ${analyticsView === 'overview' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
              onClick={() => setAnalyticsView('overview')}
            >
              Overview
            </button>
            <button
              className={`admin-btn ${analyticsView === 'transactions' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
              onClick={() => setAnalyticsView('transactions')}
            >
              Transactions
            </button>
            <button
              className={`admin-btn ${analyticsView === 'users' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
              onClick={() => setAnalyticsView('users')}
            >
              Users
            </button>
          </div>
        </div>
      </div>

      {potDistributionLoading ? (
        <div className="analytics-loading">
          <div className="loading-spinner">Loading tournament pot distribution data...</div>
        </div>
      ) : potDistributionError ? (
        <div className="analytics-error">
          <div className="error-message">Error loading pot distribution: {potDistributionError.message}</div>
        </div>
      ) : potDistributionData ? (
        <div className="analytics-content">
          {/* Overview View */}
          {analyticsView === 'overview' && (
            <div className="tournament-pot-distribution-overview">
              <div className="tournament-info-header">
                <h2>{potDistributionData.tournament.name}</h2>
                <div className="tournament-meta">
                  <span>Status: <strong>{potDistributionData.tournament.status}</strong></span>
                  <span>Max Players: <strong>{potDistributionData.tournament.maxPlayers || 'Unlimited'}</strong></span>
                  <span>Created: <strong>{new Date(potDistributionData.tournament.createdAt).toLocaleDateString('en-US')}</strong></span>
                  <span>Distribution: <strong>{new Date(potDistributionData.potDistribution.distributionTimestamp).toLocaleString('en-US')}</strong></span>
                </div>
              </div>

              <div className="admin-stats">
                <div className="admin-stat-card total-collected">
                  <div className="admin-stat-icon">💰</div>
                  <div className="admin-stat-value">${potDistributionData.summary.totalBuyIns.toLocaleString()}</div>
                  <div className="admin-stat-label">Total Buy-ins</div>
                  <div className="admin-stat-change">From {potDistributionData.summary.totalBets} bets by {potDistributionData.summary.uniqueUsers} users</div>
                </div>

                <div className="admin-stat-card total-paid">
                  <div className="admin-stat-icon">🏆</div>
                  <div className="admin-stat-value">${potDistributionData.summary.toUsers.toLocaleString()}</div>
                  <div className="admin-stat-label">Paid to Users</div>
                  <div className="admin-stat-change">Prize payouts to purchased players</div>
                </div>

                <div className="admin-stat-card commission">
                  <div className="admin-stat-icon">💸</div>
                  <div className="admin-stat-value">${potDistributionData.summary.commission.toLocaleString()}</div>
                  <div className="admin-stat-label">ProSkins Commission</div>
                  <div className="admin-stat-change">20% of total buy-ins</div>
                </div>

                <div className="admin-stat-card remaining-pot">
                  <div className="admin-stat-icon">💼</div>
                  <div className="admin-stat-value">${potDistributionData.summary.toSystem.toLocaleString()}</div>
                  <div className="admin-stat-label">To System</div>
                  <div className="admin-stat-change">Unpurchased players + remaining pot</div>
                </div>
              </div>

              {/* Skins Distribution Table */}
              {potDistributionData.potDistribution.skins && potDistributionData.potDistribution.skins.length > 0 && (
                <div className="admin-card">
                  <div className="admin-card-header">
                    <h3 className="admin-card-title">Skins Distribution</h3>
                    <div className="admin-card-subtitle">Prize distribution for skin winners</div>
                  </div>
                  <div className="admin-table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Skins Won</th>
                          <th>Amount Received</th>
                          <th>Recipient</th>
                          <th>Holes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {potDistributionData.potDistribution.skins.map((skin, index) => (
                          <tr key={index}>
                            <td>{skin.golferName}</td>
                            <td>{skin.skinsWon}</td>
                            <td>${skin.amountReceived.toFixed(2)}</td>
                            <td>
                              {skin.userId ? (
                                <span className="badge badge-success">User</span>
                              ) : (
                                <span className="badge badge-warning">System</span>
                              )}
                            </td>
                            <td>{skin.holes ? skin.holes.join(', ') : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Saw-off Distribution Table */}
              {potDistributionData.potDistribution.sawOff && potDistributionData.potDistribution.sawOff.length > 0 && (
                <div className="admin-card">
                  <div className="admin-card-header">
                    <h3 className="admin-card-title">Saw-off Distribution</h3>
                    <div className="admin-card-subtitle">Prize distribution for saw-off winners</div>
                  </div>
                  <div className="admin-table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Hole</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Recipient</th>
                        </tr>
                      </thead>
                      <tbody>
                        {potDistributionData.potDistribution.sawOff.map((sawOff, index) => (
                          <tr key={index}>
                            <td>{sawOff.golferName}</td>
                            <td>{sawOff.hole}</td>
                            <td>${sawOff.prizeReceived.toFixed(2)}</td>
                            <td>
                              {sawOff.wasPaid ? (
                                <span className="badge badge-success">Paid</span>
                              ) : sawOff.wasSkipped ? (
                                <span className="badge badge-danger">Skipped</span>
                              ) : (
                                <span className="badge badge-secondary">Unknown</span>
                              )}
                            </td>
                            <td>
                              {sawOff.userId ? (
                                <span className="badge badge-success">User</span>
                              ) : (
                                <span className="badge badge-warning">System</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Transactions View */}
          {analyticsView === 'transactions' && (
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">Transaction History</h3>
                <div className="admin-card-subtitle">
                  Total Transactions: <strong>{potDistributionData.transactions.length}</strong>
                </div>
              </div>
              
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>User</th>
                      <th>Amount</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {potDistributionData.transactions.map((transaction, index) => (
                      <tr key={transaction.id} className="transaction-row">
                        <td>{new Date(transaction.createdAt).toLocaleDateString('en-US')}</td>
                        <td>
                          <span className={`tier-badge tier-${transaction.type.toLowerCase().replace('_', '-')}`}>
                            {transaction.type.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          {transaction.user ? (
                            <div>
                              <div>{transaction.user.name}</div>
                              <div className="text-muted">{transaction.user.email}</div>
                            </div>
                          ) : (
                            <span className="text-muted">System</span>
                          )}
                        </td>
                        <td className="stats-cell">${transaction.amount.toLocaleString()}</td>
                        <td>{transaction.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Users View */}
          {analyticsView === 'users' && (
            <div className="admin-card">
              <div className="admin-card-header">
                <h3 className="admin-card-title">User Participation Summary</h3>
                <div className="admin-card-subtitle">
                  Total Users: <strong>{potDistributionData.userBets.length}</strong>
                </div>
              </div>

              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Golfer</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {potDistributionData.userBets.map((bet, index) => (
                      <tr key={bet.id}>
                        <td>
                          <div className="player-name">
                            <div>
                              <div style={{fontWeight: '600', color: '#1f2937'}}>{bet.user.name}</div>
                              <div style={{fontSize: '0.875rem', color: '#6b7280'}}>{bet.user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>{bet.golfer.name}</td>
                        <td className="stats-cell">${bet.amount.toLocaleString()}</td>
                        <td>
                          <span className={`tier-badge ${bet.status.toLowerCase() === 'completed' ? 'tier-a' : 'tier-c'}`}>
                            {bet.status}
                          </span>
                        </td>
                        <td>{new Date(bet.createdAt).toLocaleDateString('en-US')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="no-data">
          <p>No pot distribution data available for this tournament.</p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
